import type React from 'react';
import {Box, Divider, Link, Paper, Skeleton, Stack, Typography} from '@mui/material';
import {ChevronRight, TrendingDown, TrendingFlat, TrendingUp} from '@mui/icons-material';
import {Tag} from '~/components/tag/Tag';
import {pct as scPct, unitPrice, VERDICT} from '../../domain/shouldCost';
import {useMarketQuery} from '../../hooks/useMarket';
import {useShouldCostQuery} from '../../hooks/useShouldCost';
import type {MarketDriver} from '../../types/marketTypes';
import {ProfileSection} from '../components/ProfileSection';
import {OVERVIEW_HELP} from '../../domain/overviewHelp';

type Props = {databagId: number; path: string; openTab: (tab: string) => void; money: (v: number) => string};

const CARD = {p: 2, borderRadius: 2, minWidth: 0, height: '100%'} as const;

const TabLink: React.FC<{label: string; onClick: () => void}> = ({label, onClick}) => (
    <Link component="button" underline="hover" variant="body2" onClick={onClick}
          sx={{display: 'inline-flex', alignItems: 'center', ml: 'auto', color: 'text.primary'}}>
        {label}<ChevronRight fontSize="small"/>
    </Link>
);

function YoyChip({value}: {value: number | null}) {
    if (value === null) return <Typography variant="caption" color="text.secondary">—</Typography>;
    const Icon = value > 0.5 ? TrendingUp : value < -0.5 ? TrendingDown : TrendingFlat;
    const color = value > 0.5 ? 'success.dark' : value < -0.5 ? 'error.main' : 'text.secondary';
    return (
        <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, border: '1px solid', borderColor: 'divider',
            borderRadius: 1, px: 0.75, py: 0.25, color}}>
            <Icon sx={{fontSize: 14}}/>
            <Typography variant="caption" sx={{fontWeight: 600}}>{`${value > 0 ? '+' : ''}${value.toFixed(0)}% YoY`}</Typography>
        </Box>
    );
}

function DriverRow({d}: {d: MarketDriver}) {
    const confirmed = d.status === 'confirmed';
    return (
        <Stack direction="row" alignItems="center" gap={1} sx={{py: 0.75}}>
            <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: confirmed ? 'success.main' : 'warning.main', flexShrink: 0}}/>
            <Typography variant="body2" noWrap sx={{minWidth: 0}} title={d.series.name}>{d.series.name}</Typography>
            {!confirmed && <Tag size="small" solid={false} color="warning" label="Unconfirmed"/>}
            <Typography variant="body2" color="text.secondary" sx={{ml: 'auto'}}>{`${Math.round(d.weight * 100)}%`}</Typography>
            <YoyChip value={d.yoyPct}/>
        </Stack>
    );
}

/**
 * Cost intelligence (spec 4.4): the category's active market drivers (from the Market index tab) and the active price
 * model's should cost against the latest quote, with the annual saving when the quote is over should cost.
 */
export const CostIntelligenceSection: React.FC<Props> = ({databagId, path, openTab, money}) => {
    const {data, isLoading, isError} = useMarketQuery(databagId, path);
    const summary = useShouldCostQuery(databagId, path).data?.summary ?? null;
    const drivers = data?.drivers ?? [];
    const unconfirmed = drivers.filter(d => d.status === 'suggested').length;

    return (
        <ProfileSection title="Cost intelligence" help={OVERVIEW_HELP.costIntelligence} bare>
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: '1fr 1fr'}, gap: 2}}>
                <Paper variant="outlined" sx={CARD}>
                    <Stack direction="row" alignItems="center" sx={{mb: 1}}>
                        <Typography variant="subtitle2" component="h3" sx={{fontWeight: 600}}>Active drivers</Typography>
                        <TabLink label="Market index" onClick={() => openTab('market-index')}/>
                    </Stack>
                    {isLoading && <Skeleton variant="rounded" height={96}/>}
                    {isError && <Typography variant="body2" color="error.main">Could not load the market drivers.</Typography>}
                    {data && drivers.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            No market drivers with data for this category yet. Add one on the Market index tab.
                        </Typography>
                    )}
                    {drivers.length > 0 && (
                        <Stack divider={<Divider flexItem/>}>
                            {drivers.map(d => <DriverRow key={d.series.id} d={d}/>)}
                        </Stack>
                    )}
                    {unconfirmed > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1}}>
                            {`${unconfirmed} suggested driver${unconfirmed === 1 ? '' : 's'} to confirm on the Market index tab.`}
                        </Typography>
                    )}
                </Paper>
                <Paper variant="outlined" sx={CARD}>
                    <Stack direction="row" alignItems="center" sx={{mb: 1}}>
                        <Typography variant="subtitle2" component="h3" sx={{fontWeight: 600}}>Should cost vs quoted</Typography>
                        <TabLink label="Should cost" onClick={() => openTab('should-cost')}/>
                    </Stack>
                    <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', border: '1px solid', borderColor: 'divider',
                        borderRadius: 1.5, mb: 1.5}}>
                        {[
                            ['Should cost', summary ? unitPrice(summary.shouldCost, summary.currency) : null, 'text.primary'],
                            ['Quoted', summary?.quote ? unitPrice(summary.quote.unitPrice, summary.currency) : null, 'text.primary'],
                            ['Deviation', summary?.quote ? scPct(summary.quote.gapPct) : null,
                                summary?.quote?.verdict ? `${VERDICT[summary.quote.verdict].color}.main` : 'text.primary'],
                        ].map(([label, value, color], i) => (
                            <Box key={label} sx={{p: 1.5, borderLeft: i ? '1px solid' : 'none', borderColor: 'divider', minWidth: 0}}>
                                <Typography variant="caption" color="text.secondary">{label}</Typography>
                                <Typography variant="h5" color={value ? color ?? 'text.primary' : 'text.disabled'} noWrap>{value ?? '—'}</Typography>
                            </Box>
                        ))}
                    </Box>
                    {!summary ? (
                        <Typography variant="body2" color="text.secondary">
                            No active price model for this category. Start one on the Should cost tab to compare quotes with what
                            the market drivers say the price should be.
                        </Typography>
                    ) : summary.opportunity ? (
                        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2" sx={{fontWeight: 600}}>
                                {`Estimated ${money(summary.annualSaving)} annual savings if negotiated to should cost`}
                            </Typography>
                            <TabLink label="View in Should cost" onClick={() => openTab('should-cost')}/>
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {summary.quote ? `${summary.quote.supplier}'s quote is in line with should cost (${summary.modelName}).`
                                : `${summary.modelName}: add a supplier quote on the Should cost tab to compare.`}
                        </Typography>
                    )}
                </Paper>
            </Box>
        </ProfileSection>
    );
};
